import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ItemDto,
  TaskDirection,
  TaskState,
  TaskType,
} from '../../models/Task/Task';
import { taskApi } from '../../services/taskApi';
import { loadTaskTypeDictionary } from '../../services/taskTypeDictionary';

export type JournalFilterValue = string | string[];

export interface JournalFilters {
  dateRange: [Date, Date];
  textSearch: string;
  status: JournalFilterValue;
  scenario: JournalFilterValue;
  medicalOrganization: JournalFilterValue;
  result: JournalFilterValue;
  attemptStatus: JournalFilterValue;
  specialization?: JournalFilterValue;
  doctor?: JournalFilterValue;
  attemptTimeRange?: JournalFilterValue;
  campaign?: JournalFilterValue;
  pageIndex: number;
  pageSize: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
}

export interface JournalDictionaryOption {
  value: string;
  id: string;
  label: string;
}

export interface JournalDictionaries {
  statuses: JournalDictionaryOption[];
  results: JournalDictionaryOption[];
  scenarios: JournalDictionaryOption[];
}

export type JournalScenarioLabelMap = Readonly<Record<string, string>>;
export type JournalResultLabelMap = Readonly<Record<string, string>>;

export type JournalDirection = TaskDirection;

const emptyDictionaries: JournalDictionaries = {
  statuses: [],
  results: [],
  scenarios: [],
};

const hasSelectedValue = (value: JournalFilterValue) => {
  if (Array.isArray(value)) return value.length > 0;
  return value.trim().length > 0;
};

const toOption = <T extends string>(
  item: ItemDto<T>,
): JournalDictionaryOption => ({
  value: item.key,
  id: item.key,
  label: item.value ?? item.key,
});

const toLabelMap = (
  options: JournalDictionaryOption[],
): JournalScenarioLabelMap =>
  options.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

export const countActiveFilters = (
  filters: JournalFilters,
  direction: JournalDirection = 'outbound',
) => {
  const selectedFilters: JournalFilterValue[] = [
    filters.scenario,
    filters.medicalOrganization,
    filters.result,
    filters.attemptStatus,
    filters.campaign ?? '',
  ];

  if (direction === 'outbound') {
    selectedFilters.push(
      filters.status,
      filters.specialization ?? '',
      filters.doctor ?? '',
      filters.attemptTimeRange ?? '',
    );
  }

  return selectedFilters.filter(hasSelectedValue).length;
};

export const useJournalQueryState = (
  initialFilters: JournalFilters,
  direction: JournalDirection,
) => {
  const initialDateRangeRef = useRef<[Date, Date]>(initialFilters.dateRange);
  const [filters, setFilters] = useState<JournalFilters>(initialFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [dictionaries, setDictionaries] =
    useState<JournalDictionaries>(emptyDictionaries);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const scenarioRequest = loadTaskTypeDictionary(direction);

    Promise.all([
      scenarioRequest,
      taskApi.getTaskStates(),
      taskApi.getTaskResults(),
    ])
      .then(([scenarioTypes, states, results]) => {
        if (!isMounted) return;

        setDictionaries({
          scenarios: scenarioTypes.map((item: ItemDto<TaskType>) =>
            toOption(item),
          ),
          statuses: states.map((item: ItemDto<TaskState>) => toOption(item)),
          results: results.map((item: ItemDto<string>) => toOption(item)),
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setDictionaries(emptyDictionaries);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [direction]);

  const setFilter = useCallback(
    <K extends keyof JournalFilters>(key: K, value: JournalFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      dateRange: initialDateRangeRef.current,
      textSearch: '',
      status: '',
      scenario: '',
      medicalOrganization: '',
      result: '',
      attemptStatus: '',
      specialization: '',
      doctor: '',
      campaign: '',
      pageIndex: 0,
    }));
  }, []);

  const activeFilterCount = useMemo(
    () => countActiveFilters(filters, direction),
    [direction, filters],
  );

  const scenarioLabelByType = useMemo(
    () => toLabelMap(dictionaries.scenarios),
    [dictionaries.scenarios],
  );

  const resultLabelByValue = useMemo(
    () => toLabelMap(dictionaries.results),
    [dictionaries.results],
  );

  return {
    filters,
    setFilter,
    resetFilters,
    activeFilterCount,
    isLoading,
    dictionaries,
    scenarioLabelByType,
    resultLabelByValue,
  };
};
